import { ColumnDef } from '@tanstack/react-table';
import { Eye, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { ConfirmDialog, MessageDialog } from '../components/ui/alert-dialog-helper';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { createSortableColumn } from '../components/ui/column-helpers';
import { DataTable } from '../components/ui/data-table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { Order, OrderStats, OrderStatus } from '../types/order';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [stats, setStats] = useState<OrderStats | null>(null);
  const [statusFilter, setStatusFilter] = useState<OrderStatus | null>(null);

  // Server-side pagination and sorting state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    loadStats();
  }, []);

  useEffect(() => {
    loadOrders();
  }, [page, limit, sortBy, sortOrder, debouncedSearch, statusFilter]);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/orders/stats', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Error loading order stats:', err);
    }
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder,
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (statusFilter) params.set('status', statusFilter);

      const response = await fetch(`/api/orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setOrders(data.data || []);
        setPagination(data.pagination || { total: 0, page: 1, limit: 10, pages: 0 });
      }
    } catch (err) {
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const handleSortingChange = (newSortBy: string, newSortOrder: 'ASC' | 'DESC') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setPage(1); // Reset to first page when sorting changes
  };

  const handleFilterClick = (status: OrderStatus | null) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handleDeleteClick = (order: Order) => {
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!orderToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/orders/${orderToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (data.success) {
        setDeleteDialogOpen(false);
        setOrderToDelete(null);
        loadOrders();
      } else {
        setDeleteDialogOpen(false);
        setErrorMessage(data.message || 'Failed to delete order');
        setErrorDialogOpen(true);
      }
    } catch (err) {
      console.error('Error deleting order:', err);
      setDeleteDialogOpen(false);
      setErrorMessage('An error occurred while deleting the order');
      setErrorDialogOpen(true);
    }
  };

  const getStatusBadgeVariant = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.COMPLETED:
        return 'default' as const;
      case OrderStatus.IN_PROGRESS:
        return 'warning' as const;
      case OrderStatus.CONFIRMED:
        return 'secondary' as const;
      case OrderStatus.CANCELLED:
        return 'destructive' as const;
      case OrderStatus.DRAFT:
        return 'outline' as const;
      case OrderStatus.ON_HOLD:
        return 'outline' as const;
      default:
        return 'secondary' as const;
    }
  };

  const columns: ColumnDef<Order>[] = [
    {
      ...createSortableColumn<Order>('order_number', 'Order Number'),
      cell: ({ row }) => (
        <button
          onClick={() => navigate(`/orders/${row.original.id}`)}
          className="font-medium text-gray-900 hover:text-gray-700 underline hover:no-underline"
        >
          {row.original.order_number}
        </button>
      ),
    },
    {
      ...createSortableColumn<Order>('customer_name', 'Customer'),
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.customer_name}</div>
          {row.original.customer_contact && (
            <div className="text-sm text-muted-foreground">{row.original.customer_contact}</div>
          )}
        </div>
      ),
    },
    {
      ...createSortableColumn<Order>('order_date', 'Order Date'),
      cell: ({ row }) => {
        const value = row.getValue('order_date') as string;
        if (!value) return <span className="text-muted-foreground">-</span>;
        const date = new Date(value);
        if (isNaN(date.getTime())) return <span className="text-muted-foreground">-</span>;
        return date.toLocaleDateString();
      },
    },
    {
      ...createSortableColumn<Order>('required_by_date', 'Required By'),
      cell: ({ row }) => {
        const value = row.getValue('required_by_date') as string;
        if (!value) return <span className="text-muted-foreground">-</span>;
        const date = new Date(value);
        if (isNaN(date.getTime())) return <span className="text-muted-foreground">-</span>;
        return date.toLocaleDateString();
      },
    },
    {
      ...createSortableColumn<Order>('status', 'Status'),
      cell: ({ row }) => (
        <Badge variant={getStatusBadgeVariant(row.original.status)}>
          {row.original.status.replace('_', ' ').toUpperCase()}
        </Badge>
      ),
    },
    {
      accessorKey: 'jobs',
      header: 'Jobs',
      enableSorting: false,
      cell: ({ row }) => {
        const jobCount = row.original.jobs?.length || 0;
        return <span className="text-sm">{jobCount} {jobCount === 1 ? 'job' : 'jobs'}</span>;
      },
    },
    {
      ...createSortableColumn<Order>('created_at', 'Created At'),
      cell: ({ row }) => {
        const value = row.getValue('created_at') as string;
        if (!value) return <span className="text-muted-foreground">-</span>;
        const date = new Date(value);
        if (isNaN(date.getTime())) return <span className="text-muted-foreground">-</span>;
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate(`/orders/${row.original.id}`)}>
              <Eye className="h-4 w-4 mr-2" />
              View
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate(`/orders/${row.original.id}/edit`)}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600"
              onClick={() => handleDeleteClick(row.original)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="orders" />
      <main className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Orders</h1>
          <Button onClick={() => navigate('/orders/create')}>
            <Plus className="h-4 w-4 mr-2" />
            Create Order
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div
            onClick={() => handleFilterClick(null)}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              statusFilter === null ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-primary">{stats?.total || 0}</div>
            <div className="text-sm text-muted-foreground">Total Orders</div>
          </div>
          <div
            onClick={() => handleFilterClick(OrderStatus.PENDING)}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              statusFilter === OrderStatus.PENDING ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-yellow-600">{stats?.pending || 0}</div>
            <div className="text-sm text-muted-foreground">Pending</div>
          </div>
          <div
            onClick={() => handleFilterClick(OrderStatus.IN_PROGRESS)}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              statusFilter === OrderStatus.IN_PROGRESS ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-blue-600">{stats?.in_progress || 0}</div>
            <div className="text-sm text-muted-foreground">In Progress</div>
          </div>
          <div
            onClick={() => handleFilterClick(OrderStatus.COMPLETED)}
            className={`p-6 rounded-lg shadow-sm cursor-pointer transition-all border border-transparent hover:border-primary ${
              statusFilter === OrderStatus.COMPLETED ? 'bg-primary/5' : 'bg-card'
            }`}
          >
            <div className="text-2xl font-bold text-green-600">{stats?.completed || 0}</div>
            <div className="text-sm text-muted-foreground">Completed</div>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={orders}
          loading={loading}
          searchKey="order_number"
          searchPlaceholder="Search orders..."
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          emptyMessage="No orders found"
          serverSide={true}
          pagination={pagination}
          onPaginationChange={handlePaginationChange}
          onSortingChange={handleSortingChange}
        />
      </main>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Order"
        description={`Are you sure you want to delete order ${orderToDelete?.order_number}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        variant="destructive"
      />

      {/* Error Message Dialog */}
      <MessageDialog
        open={errorDialogOpen}
        onOpenChange={setErrorDialogOpen}
        title="Error"
        description={errorMessage}
        variant="destructive"
      />
    </div>
  );
};

export default Orders;
